import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Plus, Hash, MessageCircle, Moon, Zap, Clock, Calendar } from 'lucide-react';
import { AutoReplyTriggerType, AutoReplyTriggerConfig, ActiveSchedule, SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { cn } from '@/lib/core/utils';

interface RuleConditionBuilderProps {
  triggerType: AutoReplyTriggerType;
  triggerConfig: AutoReplyTriggerConfig;
  platforms: SocialPlatform[];
  activeSchedule: ActiveSchedule;
  onTriggerTypeChange: (type: AutoReplyTriggerType) => void;
  onTriggerConfigChange: (config: AutoReplyTriggerConfig) => void;
  onPlatformsChange: (platforms: SocialPlatform[]) => void;
  onActiveScheduleChange: (schedule: ActiveSchedule) => void;
}

const TRIGGER_TYPES: { value: AutoReplyTriggerType; label: string; description: string; icon: React.ElementType }[] = [
  {
    value: 'keyword',
    label: 'Keyword Match',
    description: 'Reply when message contains specific keywords',
    icon: Hash,
  },
  {
    value: 'first_message',
    label: 'First Message',
    description: 'Reply to the first message from a new contact',
    icon: MessageCircle,
  },
  {
    value: 'out_of_hours',
    label: 'Out of Hours',
    description: 'Reply when you receive messages outside business hours',
    icon: Moon,
  },
  {
    value: 'all',
    label: 'All Messages',
    description: 'Reply to every incoming message (use with caution)',
    icon: Zap,
  },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export const RuleConditionBuilder: React.FC<RuleConditionBuilderProps> = ({
  triggerType,
  triggerConfig,
  platforms,
  activeSchedule,
  onTriggerTypeChange,
  onTriggerConfigChange,
  onPlatformsChange,
  onActiveScheduleChange,
}) => {
  const [newKeyword, setNewKeyword] = useState('');
  const [showSchedule, setShowSchedule] = useState(
    Boolean(activeSchedule.days?.length || activeSchedule.start_time || activeSchedule.end_time)
  );

  const handleAddKeyword = () => {
    if (!newKeyword.trim()) return;

    const keywords = triggerConfig.keywords || [];
    if (!keywords.includes(newKeyword.toLowerCase().trim())) {
      onTriggerConfigChange({
        ...triggerConfig,
        keywords: [...keywords, newKeyword.toLowerCase().trim()],
      });
    }
    setNewKeyword('');
  };

  const handleRemoveKeyword = (keyword: string) => {
    const keywords = triggerConfig.keywords || [];
    onTriggerConfigChange({
      ...triggerConfig,
      keywords: keywords.filter(k => k !== keyword),
    });
  };

  const handlePlatformToggle = (platform: SocialPlatform) => {
    if (platforms.includes(platform)) {
      if (platforms.length > 1) {
        onPlatformsChange(platforms.filter(p => p !== platform));
      }
    } else {
      onPlatformsChange([...platforms, platform]);
    }
  };

  const handleDayToggle = (day: number) => {
    const days = activeSchedule.days || [];
    if (days.includes(day)) {
      onActiveScheduleChange({
        ...activeSchedule,
        days: days.filter(d => d !== day),
      });
    } else {
      onActiveScheduleChange({
        ...activeSchedule,
        days: [...days, day].sort(),
      });
    }
  };

  const handleScheduleToggle = (enabled: boolean) => {
    setShowSchedule(enabled);
    if (!enabled) {
      onActiveScheduleChange({});
    } else {
      onActiveScheduleChange({
        days: [1, 2, 3, 4, 5], // Mon-Fri default
        start_time: '09:00',
        end_time: '17:00',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Trigger Type Selection */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">When to trigger</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TRIGGER_TYPES.map(({ value, label, description, icon: Icon }) => (
            <div
              key={value}
              className={cn(
                'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all',
                triggerType === value
                  ? 'border-primary bg-primary/5'
                  : 'border-muted hover:border-muted-foreground/30'
              )}
              onClick={() => onTriggerTypeChange(value)}
            >
              <div className={cn(
                'p-2 rounded-full',
                triggerType === value ? 'bg-primary text-primary-foreground' : 'bg-muted'
              )}>
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium">{label}</p>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Keyword configuration (only for keyword trigger) */}
      {triggerType === 'keyword' && (
        <div className="space-y-3">
          <Label className="text-base font-semibold">Keywords</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Enter a keyword..."
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
            />
            <Button type="button" onClick={handleAddKeyword}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {(triggerConfig.keywords?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-2">
              {triggerConfig.keywords?.map((keyword) => (
                <Badge key={keyword} variant="secondary" className="px-3 py-1">
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="ml-2 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="space-y-2">
              <Label>Match type</Label>
              <Select
                value={triggerConfig.match_type || 'contains'}
                onValueChange={(value) => onTriggerConfigChange({
                  ...triggerConfig,
                  match_type: value as 'contains' | 'exact' | 'starts_with',
                })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">Contains</SelectItem>
                  <SelectItem value="exact">Exact match</SelectItem>
                  <SelectItem value="starts_with">Starts with</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2 pt-6">
              <Checkbox
                id="case-sensitive"
                checked={triggerConfig.case_sensitive || false}
                onCheckedChange={(checked) => onTriggerConfigChange({
                  ...triggerConfig,
                  case_sensitive: checked as boolean,
                })}
              />
              <Label htmlFor="case-sensitive" className="font-normal">
                Case sensitive
              </Label>
            </div>
          </div>
        </div>
      )}

      {/* Platform Selection */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Apply to platforms</Label>
        <div className="flex flex-wrap gap-3">
          {(['instagram', 'facebook'] as SocialPlatform[]).map((platform) => {
            const info = PLATFORM_INFO[platform];
            const isSelected = platforms.includes(platform);

            return (
              <div
                key={platform}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-muted-foreground/30'
                )}
                onClick={() => handlePlatformToggle(platform)}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: info.color }}
                >
                  {platform === 'instagram' ? 'IG' : 'FB'}
                </div>
                <span className="font-medium">{info.name}</span>
              </div>
            );
          })}
        </div>
        <p className="text-sm text-muted-foreground">
          Select which platforms this rule applies to
        </p>
      </div>

      {/* Schedule (optional) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base font-semibold">Active Schedule</Label>
            <p className="text-sm text-muted-foreground">
              Limit when this rule is active
            </p>
          </div>
          <Switch
            checked={showSchedule}
            onCheckedChange={handleScheduleToggle}
          />
        </div>

        {showSchedule && (
          <Card>
            <CardContent className="pt-4 space-y-4">
              {/* Days of week */}
              <div className="space-y-2">
                <Label className="text-sm">Active days</Label>
                <div className="flex gap-2">
                  {DAYS_OF_WEEK.map(({ value, label }) => {
                    const isSelected = activeSchedule.days?.includes(value);
                    return (
                      <Button
                        key={value}
                        type="button"
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        className="w-10 h-10 p-0"
                        onClick={() => handleDayToggle(value)}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Time range */}
              <div className="flex items-center gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Start time</Label>
                  <Input
                    type="time"
                    value={activeSchedule.start_time || '09:00'}
                    onChange={(e) => onActiveScheduleChange({
                      ...activeSchedule,
                      start_time: e.target.value,
                    })}
                    className="w-32"
                  />
                </div>
                <div className="pt-6">to</div>
                <div className="space-y-2">
                  <Label className="text-sm">End time</Label>
                  <Input
                    type="time"
                    value={activeSchedule.end_time || '17:00'}
                    onChange={(e) => onActiveScheduleChange({
                      ...activeSchedule,
                      end_time: e.target.value,
                    })}
                    className="w-32"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RuleConditionBuilder;
